import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { Textarea } from "../ui/textarea";

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type ReferenceInfoSheetPayload = {
  initialReferenceInfo?: string;
};

export type ReferenceInfoSheetResult = {
  referenceInfo: string;
};

function ReferenceInfoSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    ReferenceInfoSheetPayload,
    ReferenceInfoSheetResult
  >(SheetIds.REFERENCE_INFO);
  const responsiveWidth = useResponsiveWidth();
  const { initialReferenceInfo } = sheet?.payload ?? {};
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;
  const [referenceInfo, setReferenceInfo] = useState(
    initialReferenceInfo ?? ""
  );

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
      if (index === 0) {
        setReferenceInfo(initialReferenceInfo);
      }
    },
    [dismiss, initialReferenceInfo]
  );

  const onSave = useCallback(async () => {
    close({
      referenceInfo,
    });
  }, [close, referenceInfo]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onSave}
      />
    ),
    [onSave]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetView>
        <View className="bg-teachLoop-creamBase rounded-t-[20]">
          <View className="h-6" />
          <View className="mx-5 flex-row justify-between">
            <Text className="font-geologica-medium text-black text-xl leading-[1.5] flex-1">
              Reference information
            </Text>
            <Pressable onPress={onSave} className="active:opacity-80">
              <CloseIcon
                className="size-6"
                pathClassName="stroke-teachLoop-textPrimary"
              />
            </Pressable>
          </View>
          <View className="h-6" />

          <View className="mx-5">
            <Textarea
              placeholder={`Tell us what makes you a great tutor. You can include:
- Other services offered
- Wellbeing support
- SEND experience
- Parent support
- Courses or training
- LinkedIn or portfolio link`}
              size="sm"
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              value={referenceInfo}
              onChangeText={setReferenceInfo}
              className="h-[176px]"
            />
          </View>
          <View className="h-8" />
        </View>
      </BottomSheetView>
    </BottomSheet>
  ) : null;
}

export default ReferenceInfoSheet;
